import { useState, useRef } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BackupStatusPanel } from "@/components/admin/backup-status-panel";
import { Download, Loader2, DatabaseBackup, Upload } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/backup")({
  component: BackupPage,
});

function BackupPage() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  async function handleDownload() {
    setDownloading(true);
    try {
      const BASE_URL = import.meta.env.VITE_API_URL || "";
      const response = await fetch(`${BASE_URL}/api/admin/backup/download`, {
        credentials: "include",
      });

      if (response.status === 401) {
        toast.error("Session expired. Please log in again.");
        window.location.href = "/login";
        return;
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error || "Backup failed");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pph-safety-backup-${new Date().toISOString().split("T")[0]}.zip`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success("Backup downloaded");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create backup");
    } finally {
      setDownloading(false);
    }
  }

  function openFileForConfirm(file: File | null | undefined) {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please select a .zip backup file");
      return;
    }
    setPendingFile(file);
    setConfirmText("");
    setConfirmOpen(true);
  }

  function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    openFileForConfirm(e.target.files?.[0]);
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragOver(false);
    openFileForConfirm(e.dataTransfer.files?.[0]);
  }

  async function handleRestore() {
    if (!pendingFile) return;
    setRestoring(true);
    try {
      const BASE_URL = import.meta.env.VITE_API_URL || "";
      const formData = new FormData();
      formData.append("confirm", "RESTORE");
      formData.append("file", pendingFile);

      const response = await fetch(`${BASE_URL}/api/admin/backup/restore`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error((body as { error?: string }).error || "Restore failed");
      }

      toast.success(`Restored ${body.tables?.length ?? 0} tables, ${body.totalRows ?? 0} rows`);
      setConfirmOpen(false);
      setPendingFile(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to restore backup");
    } finally {
      setRestoring(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-4">
      <BackupStatusPanel />
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <DatabaseBackup className="h-5 w-5 text-paris-red-text" />
            Manual Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Download a complete backup of every table in this app (users, projects, FLHAs,
            certifications, catalogs, and more). Login credentials and active sessions are
            excluded for security. A backup like this also runs automatically every Sunday.
          </p>
          <div className="flex justify-end">
            <Button
              onClick={handleDownload}
              disabled={downloading}
              className="bg-paris-red hover:bg-paris-red/90"
            >
              {downloading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Backing up...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download Backup
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Upload className="h-5 w-5 text-destructive" />
            Restore from Backup
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            <strong className="text-destructive">Destructive:</strong> restoring replaces all
            current data with the contents of the uploaded backup file. This cannot be undone.
          </p>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
            onDragLeave={() => setIsDragOver(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click(); }}
            className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-10 text-center cursor-pointer transition-colors ${
              isDragOver
                ? "border-destructive bg-destructive/5"
                : "border-border hover:border-destructive/50 hover:bg-destructive/5"
            }`}
          >
            <Upload className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">
              Click to choose a file, or drag and drop it here
            </p>
            <p className="text-xs text-muted-foreground">.zip backup file only</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".zip"
              onChange={handleFileSelected}
              className="hidden"
            />
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Restore from backup?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all current data and replace it with the contents of{" "}
              <strong>{pendingFile?.name}</strong>. Type <strong>RESTORE</strong> to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label htmlFor="restore-confirm" className="text-sm">
              Confirmation
            </Label>
            <Input
              id="restore-confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type RESTORE"
              className="mt-1"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setPendingFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestore}
              disabled={confirmText !== "RESTORE" || restoring}
              className="bg-destructive hover:bg-destructive/90"
            >
              {restoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring...
                </>
              ) : (
                "Restore"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
